import filterAPI from '@/api/master-data/course-mapping/filter-api.js';
import { GET_LIST_USER_BY_FILTERS } from '@/api/users/users';

const state = () => ({
  departments: [],
  divisions: [],
  positions: [],
  employees: [],
  trainings: [],
  trainingsPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  trainingMapping: [],
  selectedTrainings: [], // Array of selected training IDs
  loading: {
    departments: false,
    divisions: false,
    positions: false,
    employees: false,
    trainings: false,
    trainingMapping: false,
    saving: false
  }
});

const mutations = {
  SET_DEPARTMENTS(state, departments) {
    state.departments = departments;
  },
  SET_DIVISIONS(state, divisions) {
    state.divisions = divisions;
  },
  SET_POSITIONS(state, positions) {
    state.positions = positions;
  },
  SET_EMPLOYEES(state, employees) {
    state.employees = employees;
  },
  SET_TRAININGS(state, trainings) {
    state.trainings = trainings;
  },
  SET_TRAININGS_PAGINATION(state, pagination) {
    state.trainingsPagination = {
      total: pagination.total || 0,
      page: pagination.page || 1,
      limit: pagination.limit || 10,
      totalPage: pagination.totalPage || 0
    };
  },
  SET_TRAINING_MAPPING(state, trainingMapping) {
    state.trainingMapping = trainingMapping;
  },
  SET_SELECTED_TRAININGS(state, selectedTrainings) {
    selectedTrainings = new Set(selectedTrainings);
    state.selectedTrainings = [...selectedTrainings];
  },
  TOGGLE_TRAINING_SELECTION(state, trainingId) {
    const index = state.selectedTrainings.indexOf(trainingId);
    if (index > -1) {
      state.selectedTrainings.splice(index, 1);
    } else {
      state.selectedTrainings.push(trainingId);
    }
  },
  SET_LOADING(state, { type, loading }) {
    state.loading[type] = loading;
  },
  CLEAR_DEPARTMENTS(state) {
    state.departments = [];
  },
  CLEAR_DIVISIONS(state) {
    state.divisions = [];
  },
  CLEAR_POSITIONS(state) {
    state.positions = [];
  },
  CLEAR_EMPLOYEES(state) {
    state.employees = [];
  },
  CLEAR_SELECTED_TRAININGS(state) {
    state.selectedTrainings = [];
  }
};

const getters = {
  getDepartments: (state) => state.departments,
  getDivisions: (state) => state.divisions,
  getPositions: (state) => state.positions,
  getEmployees: (state) => state.employees,
  getTrainings: (state) => state.trainings,
  getTrainingsPagination: (state) => state.trainingsPagination,
  getTrainingMapping: (state) => state.trainingMapping,
  getSelectedTrainings: (state) => state.selectedTrainings,
  getLoading: (state) => (type) => state.loading[type] || false,
  isTrainingSelected: (state) => (trainingId) => state.selectedTrainings.includes(trainingId)
};

const actions = {
  async fetchDivisions({ commit }) {
    commit('SET_LOADING', { type: 'divisions', loading: true });
    try {
      const response = await filterAPI.getDivisions();
      commit('SET_DIVISIONS', response.data.data || []);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      commit('SET_DIVISIONS', []);
    } finally {
      commit('SET_LOADING', { type: 'divisions', loading: false });
    }
  },

  async fetchDepartments({ commit }, filters = {}) {
    commit('SET_LOADING', { type: 'departments', loading: true });
    commit('CLEAR_DEPARTMENTS');
    
    try {
      const response = await filterAPI.getDepartments(filters);
      commit('SET_DEPARTMENTS', response.data.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      commit('SET_DEPARTMENTS', []);
    } finally {
      commit('SET_LOADING', { type: 'departments', loading: false });
    }
  },

  async fetchPositions({ commit }, filters = {}) {
    commit('SET_LOADING', { type: 'positions', loading: true });
    commit('CLEAR_POSITIONS');
    
    try {
      const response = await filterAPI.getPositions(filters);
      commit('SET_POSITIONS', response.data.data || []);
    } catch (error) {
      console.error('Error fetching positions:', error);
      commit('SET_POSITIONS', []);
    } finally {
      commit('SET_LOADING', { type: 'positions', loading: false });
    }
  },

  async fetchEmployees({ commit }, filters = {}) {
    commit('SET_LOADING', { type: 'employees', loading: true });
    commit('CLEAR_EMPLOYEES');
    
    try {
      const response = await filterAPI.getEmployees(filters);
      const employees = response.data.data || [];

      if (employees.length > 0 || !filters.search) {
        commit('SET_EMPLOYEES', employees);
        return;
      }

      const fallbackResponse = await GET_LIST_USER_BY_FILTERS({
        bibOnly: true,
        search: filters.search,
        employeeLimit: 50,
      });
      commit('SET_EMPLOYEES', fallbackResponse?.data?.data?.employees || []);
    } catch (error) {
      try {
        const fallbackResponse = await GET_LIST_USER_BY_FILTERS({
          bibOnly: true,
          search: filters.search,
          employeeLimit: 50,
        });
        commit('SET_EMPLOYEES', fallbackResponse?.data?.data?.employees || []);
      } catch (fallbackError) {
        console.error('Error fetching employees:', error);
        console.error('Error fetching fallback employees:', fallbackError);
        commit('SET_EMPLOYEES', []);
      }
    } finally {
      commit('SET_LOADING', { type: 'employees', loading: false });
    }
  },

  clearDepartments({ commit }) {
    commit('CLEAR_DEPARTMENTS');
  },

  clearDivisions({ commit }) {
    commit('CLEAR_DIVISIONS');
  },

  clearPositions({ commit }) {
    commit('CLEAR_POSITIONS');
  },

  clearEmployees({ commit }) {
    commit('CLEAR_EMPLOYEES');
  },

  async fetchTrainings({ commit }, params = {}) {
    commit('SET_LOADING', { type: 'trainings', loading: true });
    try {
      const response = await filterAPI.getTrainings(params);
      // Extract data from response.data.data
      const responseData = response.data.data || {};
      const trainingsData = responseData.data || [];
      commit('SET_TRAININGS', trainingsData);
      
      // Set pagination data
      commit('SET_TRAININGS_PAGINATION', {
        total: responseData.total || 0,
        page: responseData.page || 1,
        limit: responseData.limit || 10,
        totalPage: responseData.totalPage || 0
      });
    } catch (error) {
      console.error('Error fetching trainings:', error);
      commit('SET_TRAININGS', []);
      commit('SET_TRAININGS_PAGINATION', { total: 0, page: 1, limit: 10, totalPage: 0 });
    } finally {
      commit('SET_LOADING', { type: 'trainings', loading: false });
    }
  },

  async fetchTrainingMapping({ commit }, params) {
    commit('SET_LOADING', { type: 'trainingMapping', loading: true });
    try {
      const response = await filterAPI.getTrainingMapping(params);
      const mappingData = response.data.data || [];
      commit('SET_TRAINING_MAPPING', mappingData);
      
      // Set selected trainings based on mapping
      const selectedTrainings = mappingData.map(item => item.training_id);
      commit('SET_SELECTED_TRAININGS', selectedTrainings);
    } catch (error) {
      console.error('Error fetching training mapping:', error);
      commit('SET_TRAINING_MAPPING', []);
      commit('SET_SELECTED_TRAININGS', []);
    } finally {
      commit('SET_LOADING', { type: 'trainingMapping', loading: false });
    }
  },

  async saveTrainingMapping({ commit }, data) {
    commit('SET_LOADING', { type: 'saving', loading: true });
    try {
      const response = await filterAPI.saveTrainingMapping(data);
      return response;
    } catch (error) {
      console.error('Error saving training mapping:', error);
      throw error;
    } finally {
      commit('SET_LOADING', { type: 'saving', loading: false });
    }
  },

  toggleTrainingSelection({ commit }, trainingId) {
    commit('TOGGLE_TRAINING_SELECTION', trainingId);
  },

  clearSelectedTrainings({ commit }) {
    commit('CLEAR_SELECTED_TRAININGS');
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
